import React from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Sparkles, Plus, X, Pencil, Trash2, AlertTriangle, Check } from 'lucide-react';
import { PRAYER_CATEGORIES } from './spiritualConstants';
import { CommentSection } from './CommentSection';

export function PrayersTab({
  prayers, showPrayerForm, setShowPrayerForm, prayerForm, setPrayerForm,
  editingPrayer, setEditingPrayer,
  onCreatePrayer, onUpdatePrayer, onPray, onMarkAnswered, onEditPrayer, onDeletePrayer,
  currentUserId, token,
}) {
  const resetPrayerForm = () => {
    setShowPrayerForm(false);
    setEditingPrayer(null);
    setPrayerForm({ title: '', description: '', category: 'other', is_urgent: false });
  };

  return (
    <>
      {showPrayerForm ? (
        <Card className="bg-slate-800 border-slate-700 mb-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center justify-between">
              {editingPrayer ? 'Edit Prayer Request' : 'Share a Prayer Request'}
              <Button variant="ghost" size="sm" onClick={resetPrayerForm}>
                <X className="h-4 w-4" />
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={editingPrayer ? onUpdatePrayer : onCreatePrayer} className="space-y-4">
              <div>
                <label className="text-sm text-slate-400 block mb-1">Title *</label>
                <Input value={prayerForm.title} onChange={(e) => setPrayerForm({ ...prayerForm, title: e.target.value })} required placeholder="Brief title for your prayer request" className="bg-slate-900 border-slate-600 text-white placeholder:text-slate-500" data-testid="prayer-title-input" />
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">Category</label>
                <Select value={prayerForm.category} onValueChange={(val) => setPrayerForm({ ...prayerForm, category: val })}>
                  <SelectTrigger className="bg-slate-900 border-slate-600 text-white"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRAYER_CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.icon} {cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm text-slate-400 block mb-1">Details (optional)</label>
                <textarea value={prayerForm.description} onChange={(e) => setPrayerForm({ ...prayerForm, description: e.target.value })} rows={3} className="w-full bg-slate-900 border border-slate-600 rounded-md p-3 text-white placeholder:text-slate-500" placeholder="Share more details about your prayer request..." />
              </div>
              <label className="flex items-center gap-2 text-slate-400">
                <input type="checkbox" checked={prayerForm.is_urgent} onChange={(e) => setPrayerForm({ ...prayerForm, is_urgent: e.target.checked })} className="rounded" />
                Mark as urgent
              </label>
              <div className="flex gap-2">
                <Button type="submit" className="bg-purple-600 hover:bg-purple-700" data-testid="submit-prayer-btn">
                  <Sparkles className="h-4 w-4 mr-2" />
                  {editingPrayer ? 'Update Request' : 'Share Request'}
                </Button>
                <Button type="button" variant="outline" onClick={resetPrayerForm}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      ) : (
        <Button onClick={() => setShowPrayerForm(true)} className="bg-purple-600 hover:bg-purple-700 mb-6" data-testid="add-prayer-btn">
          <Plus className="h-4 w-4 mr-2" /> Share Prayer Request
        </Button>
      )}

      <div className="space-y-4">
        {prayers.length === 0 ? (
          <Card className="bg-slate-800 border-slate-700">
            <CardContent className="p-8 text-center">
              <Sparkles className="h-12 w-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No prayer requests yet. Share what's on your heart.</p>
            </CardContent>
          </Card>
        ) : prayers.map(prayer => (
          <Card key={prayer.prayer_id} className={`bg-slate-800 border-slate-700 ${prayer.is_urgent ? 'border-l-4 border-l-red-500' : ''}`}>
            <CardContent className="p-6">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-white">{prayer.title}</h3>
                    {prayer.is_urgent && (
                      <Badge className="bg-red-600">
                        <AlertTriangle className="h-3 w-3 mr-1" /> Urgent
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-slate-400 mt-1">{prayer.user_name} • {new Date(prayer.created_at).toLocaleDateString()}</p>
                </div>
                <Badge className="bg-slate-700">
                  {PRAYER_CATEGORIES.find(c => c.value === prayer.category)?.icon} {PRAYER_CATEGORIES.find(c => c.value === prayer.category)?.label}
                </Badge>
              </div>
              {prayer.description && <p className="text-slate-300 mb-4">{prayer.description}</p>}
              <div className="flex items-center gap-3 pt-4 border-t border-slate-700 flex-wrap">
                <Button
                  variant={prayer.user_has_prayed ? "secondary" : "outline"}
                  size="sm"
                  onClick={() => onPray(prayer.prayer_id)}
                  disabled={prayer.user_has_prayed}
                  className={prayer.user_has_prayed ? "bg-purple-600/20 text-purple-400" : ""}
                >
                  <Sparkles className="h-4 w-4 mr-1" />
                  {prayer.user_has_prayed ? 'Praying' : 'Pray'}
                </Button>
                <span className="text-slate-500 text-sm">{prayer.prayer_count || 0} praying</span>
                {prayer.user_id === currentUserId && !prayer.is_answered && (
                  <Button variant="ghost" size="sm" onClick={() => onMarkAnswered(prayer.prayer_id)} className="text-green-400 hover:text-green-300">
                    <Check className="h-4 w-4 mr-1" /> Mark Answered
                  </Button>
                )}
                <div className="ml-auto flex items-center gap-2">
                  <Button variant="ghost" size="sm" onClick={() => onEditPrayer(prayer)} className="text-slate-400 hover:text-blue-400">
                    <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => onDeletePrayer(prayer.prayer_id)} className="text-slate-400 hover:text-red-400">
                    <Trash2 className="h-3.5 w-3.5 mr-1" /> Delete
                  </Button>
                </div>
              </div>
              <CommentSection itemType="prayer" itemId={prayer.prayer_id} token={token} />
            </CardContent>
          </Card>
        ))}
      </div>
    </>
  );
}
